import loadable from '@loadable/component';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Col, Row, Table } from 'reactstrap';

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import FullProduct from '../../../models/product/FullProduct';
import FullProductMatrix from '../../../models/product/FullProductMatrix';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import CurrencyStore from '../../../store/CurrencyStore';
import UIStore from '../../../store/UIStore';
import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import RequestState from '../../../types/RequestState';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import { calculateTotal, roundWithPrecision } from '../../../util/number';
import CustomCol from '../../bootstrap/CustomCol';
import Icon from '../../common/Icon';
import { initialImageOrigin } from '../../common/ImageLightbox/ImageLightboxProductImage';
import { DEFAULT_MAX_QUANTITY } from '../ProductAddToCart';
import ProductCollectionMatrixHeaderRow from '../ProductCollectionMatrixHeaderRow';
import ProductCollectionMatrixTableBody from '../ProductCollectionMatrixTableBody';
import ProductSizeGuides from '../ProductSizeGuides';
import {
  createAnalyticsProducts,
  updateProductSelection,
} from '../utils/matrixSelections';

const ImageLightbox = loadable(() =>
  import(
    /* webpackChunkName: "common" */ '../../common/ImageLightbox/ImageLightboxModal'
  )
);

const SELECTION_PUBLISH_DEBOUNCE_MS = 120;

@observer
export class ProductCollectionMatrix extends Component {
  constructor(props) {
    super(props);

    const { product } = props;

    product.sortProductImages('for_color_id');

    this.selectionUpdateTimers = {};
    this.prevMatrixSelectionRef = { current: null };
    this.inputResetTimeoutId = null;
    this.skipNextExternalInputClear = false;

    this.state = {
      mainImageIndex: null,
      lightboxIsOpen: false,
      formError: false,
      tooltipStates: {},
    };

    product.images.forEach((_, index) => {
      this.lightboxInitialState = {
        ...this.lightboxInitialState,
        [index]: {
          focus: false,
          scale: 1,
          origin: initialImageOrigin,
        },
      };
    });
  }

  getImageLightbox = () => {
    const { product } = this.props;
    const { mainImageIndex, lightboxIsOpen } = this.state;

    if (
      product.images.length === 0 ||
      !lightboxIsOpen ||
      mainImageIndex === null
    ) {
      return null;
    }

    this.lightboxInitialState.selectedImage = mainImageIndex;

    return (
      <ImageLightbox
        initialState={this.lightboxInitialState}
        mainImageIndex={mainImageIndex}
        lightboxIsOpen={lightboxIsOpen}
        product={product}
        onClick={this.toggleLightbox.bind(this, false)}
        images={product.images}
      />
    );
  };

  toggleLightbox = (lightboxIsOpen) => this.setState({ lightboxIsOpen });

  getDisplayColumnElements = () => this.props.productMatrix.getDisplayColumns();

  componentDidMount() {
    this.prevMatrixSelectionRef.current =
      this.props.cartStore.getMatrixSelection(this.props.product.id);
  }

  componentWillUnmount() {
    const { cartStore, product } = this.props;
    this.cancelPendingSelectionTimers();
    if (this.inputResetTimeoutId) {
      clearTimeout(this.inputResetTimeoutId);
      this.inputResetTimeoutId = null;
    }
    // Drop keystroke pending so remounted empty inputs cannot disagree with the store.
    cartStore.clearMatrixPendingQuantities(product.id);
  }

  componentDidUpdate() {
    const { cartStore, product } = this.props;
    const currentSelection = cartStore.getMatrixSelection(product.id);
    const hadProducts =
      this.prevMatrixSelectionRef.current &&
      this.prevMatrixSelectionRef.current.products?.length > 0;
    const nowEmpty =
      !currentSelection ||
      !currentSelection.products ||
      currentSelection.products.length === 0;
    if (hadProducts && nowEmpty) {
      if (this.skipNextExternalInputClear) {
        this.skipNextExternalInputClear = false;
        this.resetParentSelectionState();
        if (
          String(cartStore.matrixClearedFromAddToCartProductId) ===
          String(product.id)
        ) {
          cartStore.clearMatrixClearFromAddToCartFlag();
        }
      } else if (
        String(cartStore.matrixClearedFromAddToCartProductId) ===
        String(product.id)
      ) {
        // Nav already bumped the clear token before wiping selection.
        this.cancelPendingSelectionTimers();
        this.resetParentSelectionState();
        cartStore.clearMatrixClearFromAddToCartFlag();
      } else {
        this.cancelPendingSelectionTimers();
        this.clearMatrixInputs();
      }
    }
    this.prevMatrixSelectionRef.current = currentSelection;
  }

  addSelectedProducts = () => {
    const { cartStore, analytics, accountStore, product } = this.props;
    this.cancelPendingSelectionTimers();
    const productsAdded = cartStore.getEffectiveMatrixProducts(product.id);
    if (!productsAdded.length) {
      return;
    }
    const analyticsAdded = createAnalyticsProducts(
      productsAdded,
      product,
      accountStore.showPricesWithTax
    );

    // Loading + clear token first (inputs are a tiny observer; selection wipe deferred).
    cartStore.setLoading(true);
    this.skipNextExternalInputClear = true;
    cartStore.bumpMatrixInputClearToken(product.id);

    const populatePromise = cartStore.populate(
      productsAdded,
      accountStore.showCollectionProductMatrix
    );

    this.scheduleDeferredClearAfterAdd(() => {
      cartStore.clearMatrixSelection(product.id, true);
      cartStore.updateProductQuantity(0);
      this.resetParentSelectionState();
      cartStore.clearMatrixClearFromAddToCartFlag();
    });

    populatePromise.then(() => {
      const precision = 4;
      const totalValue = roundWithPrecision(
        calculateTotal(
          analyticsAdded.map((p) => p.price * p.quantity),
          precision
        ),
        precision
      );
      analytics.addToCart(analyticsAdded, totalValue);
      analytics.cartStatus({
        cart: cartStore.cart,
      });
    });
  };

  scheduleDeferredClearAfterAdd = (clearFn) => {
    if (this.inputResetTimeoutId) {
      clearTimeout(this.inputResetTimeoutId);
    }
    this.inputResetTimeoutId = setTimeout(() => {
      this.inputResetTimeoutId = null;
      clearFn();
    }, 0);
  };

  resetParentSelectionState = () => {
    this.setState({
      formError: false,
    });
  };

  clearMatrixInputs = () => {
    const { cartStore, product } = this.props;
    cartStore.bumpMatrixInputClearToken(product.id);
    this.resetParentSelectionState();
  };

  cancelPendingSelectionTimers = () => {
    Object.values(this.selectionUpdateTimers).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.selectionUpdateTimers = {};
  };

  renderProductMatrixTitles = () => {
    const titles = this.props.productMatrix.getNames();
    return (
      <Col>
        <div className="ProductCollectionMatrix__column-row-label">
          <span>
            {titles.columnName}
            {titles.columnName && titles.rowName && ' / '}
            {titles.rowName}
          </span>
        </div>
      </Col>
    );
  };

  setMainImageIndex = (mainImageIndex) => {
    this.setState({ mainImageIndex }, () => this.toggleLightbox(true));
  };

  handleQuantityNotify = ({ id, qty }) => {
    const { cartStore, product } = this.props;
    // Pending goes to the store immediately so nav/add can read it without a flusher.
    cartStore.setMatrixPendingQuantity(product.id, id, qty);
    this.scheduleSelectionUpdate({ id, quantity: qty });
  };

  scheduleSelectionUpdate({ id, quantity }) {
    if (this.selectionUpdateTimers[id]) {
      clearTimeout(this.selectionUpdateTimers[id]);
    }

    this.selectionUpdateTimers[id] = setTimeout(() => {
      delete this.selectionUpdateTimers[id];
      const { cartStore, product } = this.props;
      const maxQuantity = this.getMaxQuantity(id);
      this.validateProductQuantity(quantity, maxQuantity);
      // Prefer MST selection over this.state — sync and safe if two cell timers fire
      // before React applies the first setState.
      const committedProducts =
        cartStore.getMatrixSelection(product.id)?.products || [];
      this.findSelectedProduct(committedProducts, id, quantity);
    }, SELECTION_PUBLISH_DEBOUNCE_MS);
  }

  getMaxQuantity = (activeProductId) => {
    const { accountStore, configStore, product } = this.props;

    const maxAccountQuantity =
      accountStore.account && accountStore.account.max_product_quantity > 0
        ? accountStore.account.max_product_quantity
        : DEFAULT_MAX_QUANTITY;

    const maxBackorderQuantity =
      product.availability_type === ProductAvailabilityType.ALLOW_BACKORDER
        ? configStore.product.backorderLimit > 0
          ? configStore.product.backorderLimit
          : DEFAULT_MAX_QUANTITY
        : DEFAULT_MAX_QUANTITY;

    const maxNormalProductQuantity =
      product.availability_type !== ProductAvailabilityType.ALLOW_BACKORDER
        ? product.canBeOrderedOutOfStock
          ? DEFAULT_MAX_QUANTITY
          : this.getMaxByPackage(product, activeProductId)
        : DEFAULT_MAX_QUANTITY;

    return Math.min(
      maxAccountQuantity,
      maxBackorderQuantity,
      maxNormalProductQuantity,
      DEFAULT_MAX_QUANTITY
    );
  };

  getMaxByPackage = (product, activeProductId) => {
    return product.sellInPackage
      ? Math.floor(
          product.getFreeQuantity(activeProductId) / product.package_size
        ) * product.package_size
      : product.getFreeQuantity(activeProductId);
  };

  validateProductQuantity(quantity, maxQuantity) {
    const quantityStep = this.getQuantityStep();

    if (quantityStep > 1) {
      const validQuantity = quantityValid(maxQuantity, quantityStep);
      const validProductCount = productCountValid(quantityStep);
      const invalidQuantity =
        validQuantity(quantity) || validProductCount(quantity);

      if (invalidQuantity && quantity) {
        this.setState({
          formError: true,
        });
      }

      if (!invalidQuantity || !quantity) {
        this.setState({
          formError: false,
        });
      }
    }
  }

  getQuantityStep = () => {
    const { product } = this.props;
    return product && product.sellInPackage ? product.package_size : 1;
  };

  findSelectedProduct = (selectedProducts, id, qty) => {
    const { accountStore, cartStore, product } = this.props;
    const withTax = accountStore.showPricesWithTax;

    const newSelectedProducts = updateProductSelection(
      selectedProducts,
      id,
      qty
    );

    const analyticsProducts = createAnalyticsProducts(
      newSelectedProducts,
      product,
      withTax
    );

    const totalQuantity = newSelectedProducts.reduce(
      (sum, p) => sum + p.qty,
      0
    );

    cartStore.setMatrixSelection(
      product.id,
      newSelectedProducts,
      analyticsProducts
    );
    // Drop only this variant from pending; other cells may still be awaiting debounce.
    cartStore.setMatrixPendingQuantity(product.id, id, 0);

    cartStore.updateProductQuantity(totalQuantity);
  };

  renderAddSelectedProductsButton = (className) => {
    const { cartStore } = this.props;
    const loading = cartStore.state === RequestState.LOADING;
    return (
      <Button
        className={className}
        onClick={this.addSelectedProducts}
        color="primary"
        disabled={this.state.formError || loading}
        block
      >
        {loading ? (
          <Icon name="circle-o-notch" spin={loading} />
        ) : (
          <FormattedMessage
            id="productCollectionMatrix.addSelectedProducts"
            defaultMessage="Add Selected Products"
          />
        )}
      </Button>
    );
  };

  render() {
    const {
      accountStore,
      cartStore,
      configStore,
      uiStore,
      product,
      productMatrix,
      ifProductRowImages,
    } = this.props;
    // Observe matrix selection so external clears re-render and run clear triggers.
    cartStore.getMatrixSelection(product.id);
    const displayColumns = this.getDisplayColumnElements();
    const styles = {
      tableLayout:
        !uiStore.isMobile && displayColumns.length >= 4 ? 'fixed' : 'unset',
    };
    const showHeader =
      productMatrix.getDisplayColumns().length === 1 ||
      productMatrix.getDisplayRows().length <= 4;
    return (
      <div className="ProductCollectionMatrix">
        <Row className="ProductCollectionMatrix__info-row">
          <CustomCol>
            <p className="ProductCollectionMatrix__product-matrix-info">
              <FormattedMessage {...globalTranslations.productMatrixInfo} />
            </p>
          </CustomCol>
        </Row>
        <Row className="ProductCollectionMatrix__buttons-row">
          {!accountStore.isViewOnly && (
            <>
              <CustomCol md={3} xxl={2}>
                {this.renderAddSelectedProductsButton(
                  'ProductCollectionMatrix__info-row--add-selected-products-button'
                )}
              </CustomCol>
              <CustomCol md={9} xxl={2}>
                {product.sizeGuides.length > 0 && (
                  <ProductSizeGuides sizeGuides={product.sizeGuides} />
                )}
              </CustomCol>
            </>
          )}
        </Row>
        <Row>{this.renderProductMatrixTitles()}</Row>
        <Row>
          <Col xs={12}>
            <Table
              className="ProductCollectionMatrix__product-table"
              style={styles}
              striped={!configStore.productPage.matrixDetailedStockInfoEnabled}
            >
              {!uiStore.isMobile && showHeader && (
                <ProductCollectionMatrixHeaderRow
                  productMatrix={productMatrix}
                />
              )}
              <ProductCollectionMatrixTableBody
                product={product}
                productMatrix={productMatrix}
                ifProductRowImages={ifProductRowImages}
                tooltipStates={this.state.tooltipStates}
                onImageClick={this.setMainImageIndex}
                onQuantityNotify={this.handleQuantityNotify}
                cartStore={cartStore}
              />
            </Table>
          </Col>
        </Row>
        {!accountStore.isViewOnly && (
          <Row className="ProductCollectionMatrix__info-row-footer">
            <CustomCol
              md={{ size: 3, offset: 9 }}
              xxl={{ size: 2, offset: 10 }}
            >
              {this.renderAddSelectedProductsButton(
                'ProductCollectionMatrix__info-row-footer--add-selected-products-button'
              )}
            </CustomCol>
          </Row>
        )}
        {this.getImageLightbox()}
      </div>
    );
  }
}

ProductCollectionMatrix.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(FullProduct).isRequired,
  productMatrix: modelOf(FullProductMatrix).isRequired,
};

export default injectIntl(
  inject(
    'analytics',
    'accountStore',
    'cartStore',
    'configStore',
    'currencyStore',
    'uiStore'
  )(ProductCollectionMatrix)
);
